import React, { useState } from "react";
import { stratificationService } from "../../services/stratification.services";
import { SearchSelect } from "./SearchSelect";
import { Pencil } from "../Icons/Pencil";
import { FloppyDisk } from "../Icons/FloppyDisk";
import { TrashCan } from "../Icons/TrashCan";

const CONDITION_OPTIONS = [
  { value: "GT", label: ">" },
  { value: "LT", label: "<" },
  { value: "GTE", label: ">=" },
  { value: "LTE", label: "<=" },
  { value: "EQ", label: "==" },
];

const conditionLabel = (val) =>
  CONDITION_OPTIONS.find((o) => o.value === val)?.label || val;

export const RiskLabParametersSection = ({ items, onRefresh }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedParam, setSelectedParam] = useState(null);
  const [condition, setCondition] = useState("GT");
  const [thresholdValue, setThresholdValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editCondition, setEditCondition] = useState("");
  const [editValue, setEditValue] = useState("");

  const handleSearch = async (query) => {
    const data = await stratificationService.searchParameterDefinitions(query);
    return data.results || [];
  };

  const handleAdd = async () => {
    if (!selectedParam || !thresholdValue) return;
    setSaving(true);
    try {
      await stratificationService.createRiskLabParameter({
        parameter_definition_id: selectedParam.id,
        condition,
        threshold_value: thresholdValue,
      });
      resetAddForm();
      setIsExpanded(true);
      onRefresh();
    } catch (err) {
      console.error("Error creating risk lab parameter:", err);
    } finally {
      setSaving(false);
    }
  };

  const resetAddForm = () => {
    setSelectedParam(null);
    setCondition("GT");
    setThresholdValue("");
    setShowAddForm(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Opravdu chcete odstranit tento parametr?")) return;
    try {
      await stratificationService.deleteRiskLabParameter(id);
      setIsExpanded(true);
      onRefresh();
    } catch (err) {
      console.error("Error deleting risk lab parameter:", err);
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditCondition(item.condition);
    setEditValue(item.threshold_value || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditCondition("");
    setEditValue("");
  };

  const handleSaveEdit = async (id) => {
    setSaving(true);
    try {
      await stratificationService.updateRiskLabParameter(id, {
        condition: editCondition,
        threshold_value: editValue,
      });
      cancelEdit();
      setIsExpanded(true);
      onRefresh();
    } catch (err) {
      console.error("Error updating risk lab parameter:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="strat-section">
      <div
        className="strat-section-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3 className="strat-section-title">Laboratorní parametry</h3>
        <span className="strat-section-toggle">{isExpanded ? "−" : "+"}</span>
      </div>

      {isExpanded && (
        <div className="strat-section-content">
          <div className="strat-table-header">
            <div className="strat-header-cell strat-cell--param">Parametr</div>
            <div className="strat-header-cell strat-cell--condition">
              Podmínka
            </div>
            <div className="strat-header-cell strat-cell--value">Hodnota</div>
            <div className="strat-header-cell strat-cell--units">Jednotka</div>
            <div className="strat-header-cell strat-cell--actions" />
          </div>

          {items.length === 0 && !showAddForm && (
            <div className="strat-empty">
              Žádné laboratorní parametry nebyly přidány.
            </div>
          )}

          {items.map((item, idx) => {
            const isEditing = editingId === item.id;
            const paramDef = item.parameter_definition;

            return (
              <div
                key={item.id}
                className={`strat-row ${idx % 2 === 1 ? "strat-row--odd" : ""} ${isEditing ? "strat-row--editing" : ""}`}
              >
                <div className="strat-cell strat-cell--param">
                  {paramDef?.name || ""}{" "}
                  {/* {paramDef?.abbreviation && (
                    <span style={{ color: "#6c757d" }}>
                      ({paramDef.abbreviation})
                    </span>
                  )} */}
                </div>
                <div className="strat-cell strat-cell--condition">
                  {isEditing ? (
                    <select
                      className="strat-select"
                      value={editCondition}
                      onChange={(e) => setEditCondition(e.target.value)}
                    >
                      {CONDITION_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    conditionLabel(item.condition)
                  )}
                </div>
                <div className="strat-cell strat-cell--value">
                  {isEditing ? (
                    <input
                      type="number"
                      className="strat-input"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      step="any"
                    />
                  ) : (
                    item.threshold_value
                  )}
                </div>
                <div className="strat-cell strat-cell--units">
                  {paramDef?.units || ""}
                </div>
                <div className="strat-cell strat-cell--actions">
                  {isEditing ? (
                    <>
                      <button
                        className="strat-action-btn strat-action-btn--save"
                        onClick={() => handleSaveEdit(item.id)}
                        title="Uložit"
                      >
                        <FloppyDisk className="strat-action-icon" />
                      </button>
                      <button
                        className="strat-action-btn strat-action-btn--cancel"
                        onClick={cancelEdit}
                        title="Zrušit"
                      >
                        ✕
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="strat-action-btn strat-action-btn--edit"
                        onClick={() => startEdit(item)}
                        title="Upravit"
                      >
                        <Pencil className="strat-action-icon" />
                      </button>
                      <button
                        className="strat-action-btn strat-action-btn--delete"
                        onClick={() => handleDelete(item.id)}
                        title="Odstranit"
                      >
                        <TrashCan className="strat-action-icon" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}

          {showAddForm && (
            <div className="strat-inline-form">
              <div className="strat-inline-field strat-inline-field--grow">
                <span className="strat-inline-label">Laboratorní parametr</span>
                <SearchSelect
                  placeholder="Vyhledat parametr..."
                  onSearch={handleSearch}
                  onSelect={setSelectedParam}
                  renderOption={(item) => (
                    <>
                      <span className="strat-search-option--code">
                        {item.abbreviation}
                      </span>
                      {item.name}
                    </>
                  )}
                />
                {selectedParam && (
                  <small>
                    Vybráno: {selectedParam.abbreviation} – {selectedParam.name}
                  </small>
                )}
              </div>
              <div className="strat-inline-field">
                <span className="strat-inline-label">Podmínka</span>
                <select
                  className="strat-select"
                  value={condition}
                  onChange={(e) => setCondition(e.target.value)}
                >
                  {CONDITION_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="strat-inline-field">
                <span className="strat-inline-label">Hodnota</span>
                <input
                  type="number"
                  className="strat-input"
                  style={{ width: 120 }}
                  value={thresholdValue}
                  onChange={(e) => setThresholdValue(e.target.value)}
                  step="any"
                  placeholder="0.00"
                />
              </div>
              <div style={{ display: "flex", gap: 8, alignSelf: "flex-end" }}>
                <button
                  className="strat-add-btn"
                  onClick={handleAdd}
                  disabled={!selectedParam || !thresholdValue || saving}
                >
                  {saving ? "Ukládání..." : "Uložit"}
                </button>
                <button
                  className="strat-add-btn strat-add-btn--secondary"
                  onClick={resetAddForm}
                >
                  Zrušit
                </button>
              </div>
            </div>
          )}

          <div className="strat-add-row">
            <button
              className="strat-add-btn"
              onClick={() => setShowAddForm(true)}
              disabled={showAddForm}
            >
              + Přidat laboratorní parametr
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
